import { ClientRegistry } from '../components';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * System that triggers spatial footstep sounds based on animation frames.
 * Mirrors the logic of FootstepParticleEmitSystem for frame-event detection.
 */
export class FootstepSoundSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = [
    'Animation',
    'SpritesheetSprite',
    'VisualPosition',
    'Transform',
    'IsMoving',
  ];

  private lastEmittedFrame = new Map<number, number>();

  /**
   * Pre-loads the footstep sound asset.
   */
  constructor(clientServices: ClientServiceContainer) {
    super(clientServices);
  }

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    const spritesheet = world.getComponent(entityId, 'SpritesheetSprite')!;
    const visualPosition = world.getComponent(entityId, 'VisualPosition')!;

    const frameIndex = spritesheet.frameIndex;
    const lastEmitted = this.lastEmittedFrame.get(entityId);

    // Only attempt sound if the frame has changed since we last checked
    if (lastEmitted !== frameIndex) {
      if (lastEmitted === undefined) {
        world.registerOnEntityDestroyed(entityId, (id) => {
          this.lastEmittedFrame.delete(id);
        });
      }

      const point = this.clientServices.spritesheet.getPointForFrame(
        spritesheet.spritesheetId,
        'FootstepParticlePoint',
        frameIndex,
      );

      if (point) {
        const size = this.clientServices.spritesheet.getFrameSize(spritesheet.spritesheetId);

        if (size) {
          // Get anchor from Sprite component if available, otherwise default to bottom-center
          const sprite = world.getComponent(entityId, 'Sprite');
          const anchorX = sprite ? sprite.anchorX : 0.5;
          const anchorY = sprite ? sprite.anchorY : 1.0;

          // Calculate world position of the footstep point
          const originX = visualPosition.x - size.width * anchorX;
          const originY = visualPosition.y - size.height * anchorY;

          const emitX = originX + point.x;
          const emitY = originY + point.y;

          // Get listener position (local player)
          const localPlayerId = this.getLocalPlayerEntityId();
          if (localPlayerId !== undefined) {
            const listenerPos = world.getComponent(localPlayerId, 'VisualPosition');
            if (listenerPos) {
              this.clientServices.spatialSound.playSpatial(
                'sound/footstep.ogg',
                emitX,
                emitY,
                listenerPos.x,
                listenerPos.y,
              );
            }
          }
        }
      }

      this.lastEmittedFrame.set(entityId, frameIndex);
    }
  }
}
